import React, { useRef, useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { Button } from 'js/design_system/Button';
import Badge from 'js/design_system/Badge';
import { useReferenceEvaluation } from './hooks/useReferenceEvaluation';
import { ReferenceCard } from './components/ReferenceCard';
import { ClaimEvaluationData } from './components/ClaimEvaluationRow';
import { WorkContext } from './api';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    var(--brand-colors-neutral-200) 0%,
    var(--brand-colors-neutral-300) 100%
  );

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const Header = styled.header`
  background: var(--background-white);
  border-bottom: 1px solid var(--stroke-light);
  padding: var(--spacing-sm-24) var(--spacing-sm-32);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MainContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-sm-32);
`;

const UploadSection = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-md-48);
  text-align: center;
  border: 2px dashed var(--stroke-light);
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    border-color: var(--brand-colors-blue-600);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }

  &.dragging {
    border-color: var(--brand-colors-blue-600);
    background: var(--brand-colors-blue-100);
    transform: scale(1.01);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const UploadIcon = styled(Icon)`
  color: var(--text-inactive);
  margin-bottom: var(--spacing-xs-16);
  transition:
    color 0.2s ease,
    transform 0.2s ease;

  ${UploadSection}:hover & {
    color: var(--text-link);
    transform: scale(1.05);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorBanner = styled.div`
  background: var(--brand-colors-error-100);
  border: 1px solid var(--brand-colors-error-500);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-xs-16);
  margin-bottom: var(--spacing-sm-24);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8);
  animation: fadeInUp 0.3s ease-out;
`;

const ErrorIcon = styled(Icon)`
  color: var(--brand-colors-error-500);
  flex-shrink: 0;
`;

const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24);
`;

const ProcessingHeader = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-sm-24);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm-24);
`;

const SpinningIcon = styled(Icon)`
  animation: spin 1.5s linear infinite;
  color: var(--text-link);
`;

const ProcessingInfo = styled.div`
  flex: 1;
`;

const ProcessingStats = styled.div`
  display: flex;
  gap: var(--spacing-xs-16);
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  padding: var(--spacing-xs-4) var(--spacing-xs-12);
  background: var(--brand-colors-neutral-200);
  border-radius: var(--border-radius-radius-round);
  font-size: 13px;
  color: var(--text-secondary);
`;

const LiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--brand-colors-success-100);
  color: var(--brand-colors-success-900);
  border-radius: var(--border-radius-radius-round);
  font-size: 12px;
  font-weight: 500;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: var(--brand-colors-success-500);
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;
  }
`;

const ProgressContainer = styled.div`
  margin-top: var(--spacing-xs-8);
`;

const ProgressBar = styled.div`
  height: 6px;
  background: var(--brand-colors-neutral-300);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--brand-colors-blue-500) 0%,
    var(--brand-colors-blue-600) 100%
  );
  transition: width 0.5s ease;
  border-radius: 3px;
`;

const StatusSteps = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-sm-32);
  margin-top: var(--spacing-xs-16);
  flex-wrap: wrap;
`;

const StatusStep = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4);
  font-size: 13px;
  transition: color 0.2s ease;
`;

const StatusStepActive = styled(StatusStep)`
  color: var(--text-link);
  font-weight: 500;

  .spinning-step-icon {
    animation: spin 1.5s linear infinite;
  }
`;

const StatusStepCompleted = styled(StatusStep)`
  color: var(--brand-colors-success-500);
`;

const StatusStepPending = styled(StatusStep)`
  color: var(--text-inactive);
`;

const StreamingSection = styled.div`
  animation: fadeInUp 0.4s ease-out;
`;

const StreamingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-xs-16);
  padding-bottom: var(--spacing-xs-16);
  border-bottom: 1px solid var(--stroke-light);
`;

const ResultsSection = styled.div`
  animation: fadeInUp 0.4s ease-out;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm-24);
  padding: var(--spacing-sm-24);
  background: var(--background-white);
  border-radius: var(--border-radius-radius-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm-24);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-4);
`;

const ReferencesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16);
`;

const ReferenceCardWrapper = styled.div`
  animation: fadeInUp 0.4s ease-out;
  animation-fill-mode: both;
`;

const SkeletonCard = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-sm-24);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const SkeletonLine = styled.div`
  height: 16px;
  background: linear-gradient(
    90deg,
    var(--brand-colors-neutral-300) 25%,
    var(--brand-colors-neutral-200) 50%,
    var(--brand-colors-neutral-300) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const UploadingSection = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-md-48);
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: fadeInUp 0.3s ease-out;
`;

const WorkContextCard = styled.div`
  background: var(--background-white);
  border-radius: var(--border-radius-radius-lg);
  padding: var(--spacing-sm-24);
  margin-bottom: var(--spacing-sm-24);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: fadeInUp 0.3s ease-out;
`;

const WorkContextAuthors = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8);
  margin-top: var(--spacing-xs-4);
  flex-wrap: wrap;
`;

const WorkContextAbstract = styled.div`
  margin-top: var(--spacing-xs-12);
  padding-top: var(--spacing-xs-12);
  border-top: 1px solid var(--stroke-light);
`;

type ProcessingStep = {
  key: string;
  label: string;
  statuses: string[];
};

const PROCESSING_STEPS: ProcessingStep[] = [
  {
    key: 'document',
    label: 'Processing Document',
    statuses: ['processing_document', 'processing_grobid'],
  },
  {
    key: 'references',
    label: 'Extracting References',
    statuses: ['extracting_references'],
  },
  {
    key: 'validating',
    label: 'Validating',
    statuses: ['validating_references', 'validating'],
  },
  {
    key: 'claims',
    label: 'Extracting Claims',
    statuses: ['extracting_claims'],
  },
  {
    key: 'evaluating',
    label: 'Evaluating',
    statuses: ['evaluating_claims', 'evaluating'],
  },
];

function getProgress(status: string): number {
  const statusProgress: Record<string, number> = {
    pending: 5,
    processing_document: 15,
    processing_grobid: 20,
    extracting_references: 35,
    validating_references: 50,
    validating: 55,
    extracting_claims: 70,
    evaluating_claims: 85,
    evaluating: 90,
    completed: 100,
    error: 0,
  };
  return statusProgress[status] || 5;
}

function isStepCompleted(step: ProcessingStep, currentStatus: string): boolean {
  const statusOrder = [
    'pending',
    'processing_document',
    'processing_grobid',
    'extracting_references',
    'validating_references',
    'validating',
    'extracting_claims',
    'evaluating_claims',
    'evaluating',
    'completed',
  ];
  const currentIndex = statusOrder.indexOf(currentStatus);
  const stepLastStatusIndex = Math.max(
    ...step.statuses.map((s) => statusOrder.indexOf(s)),
  );
  return currentIndex > stepLastStatusIndex;
}

function isStepActive(step: ProcessingStep, currentStatus: string): boolean {
  return step.statuses.includes(currentStatus);
}

type ReferenceEvaluationPageProps = {
  evaluationId?: string;
};

export const ReferenceEvaluationPage: React.FC<
  ReferenceEvaluationPageProps
> = ({ evaluationId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    evaluation,
    isUploading,
    isPolling,
    error,
    uploadDocument,
    clearError,
    reset,
  } = useReferenceEvaluation({
    initialEvaluationId: evaluationId ? parseInt(evaluationId, 10) : undefined,
    onComplete: () => alert('Report evaluation complete!'),
    onError: (err) => console.error('Evaluation error:', err),
  });

  const handleFileSelect = (file: File | null) => {
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return;
    }

    uploadDocument(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Build evaluations map for references
  const evaluationsMap: Record<string, ClaimEvaluationData[]> = {};
  if (evaluation?.evaluations) {
    for (const evalResult of evaluation.evaluations) {
      if (evalResult.reference_id && evalResult.claims) {
        evaluationsMap[evalResult.reference_id] = evalResult.claims;
      }
    }
  }

  const showUpload = !evaluation && !isUploading;
  const showUploading = isUploading && !evaluation;
  const showProcessing =
    evaluation && !['completed', 'error'].includes(evaluation.status);
  const showResults = evaluation?.status === 'completed';
  const showError =
    error || (evaluation?.status === 'error' && evaluation?.error_message);

  // Check if we have streaming data to show
  const hasReferences =
    evaluation?.references && evaluation.references.length > 0;
  const referencesCount = evaluation?.references?.length || 0;
  const evaluationsCount = evaluation?.evaluations_count || 0;
  const verifiedCount = evaluation?.verified_references_count || 0;

  // Type the references properly
  type OpenalexDataType = {
    citationCount?: number;
    id?: string;
    url?: string;
    locations?: Array<{
      pdf_url?: string;
      landing_page_url?: string;
    }>;
  };

  type ReferenceType = {
    id: string;
    identifier?: string;
    title: string;
    authors: string[];
    publication_year?: number;
    publication?: string;
    doi?: string;
    verified: boolean;
    raw_reference?: string;
    openalex_data?: OpenalexDataType;
    impact_factor?: {
      impact_factor?: number;
      quartile?: string;
    };
    claims: string[];
  };

  // Type the evaluation results properly
  type EvaluationResultType = {
    reference_id: string;
    full_text_url?: string | null;
    best_source_url?: string | null;
    found_metadata?: {
      doi: string | null;
      title: string | null;
      authors: string[] | null;
      publication_year: number | null;
      publication: string | null;
    };
    metadata_source?: 'openalex' | 'web';
    validation?: {
      doi_format_valid: boolean | null;
      doi_resolves: boolean | null;
      title_similarity: number | null;
      title_similarity_threshold_met: boolean | null;
      authors_match: boolean | null;
      year_matches: boolean | null;
      publication_matches: boolean | null;
      overall_valid: boolean;
    };
    claims: Array<{
      claim_text: string;
      reasoning: string;
      quote: string | null;
      supported: string;
    }>;
  };

  // Build fullTextUrl and bestSourceUrl maps from evaluations
  const fullTextUrlMap: Record<string, string | null> = {};
  const bestSourceUrlMap: Record<string, string | null> = {};
  const foundMetadataMap: Record<
    string,
    EvaluationResultType['found_metadata']
  > = {};
  const metadataSourceMap: Record<string, 'openalex' | 'web'> = {};
  const validationMap: Record<string, EvaluationResultType['validation']> = {};

  if (evaluation?.evaluations) {
    for (const evalResult of evaluation.evaluations as EvaluationResultType[]) {
      if (evalResult.reference_id && evalResult.full_text_url) {
        fullTextUrlMap[evalResult.reference_id] = evalResult.full_text_url;
      }
      if (evalResult.reference_id && evalResult.best_source_url) {
        bestSourceUrlMap[evalResult.reference_id] = evalResult.best_source_url;
      }
      if (evalResult.reference_id && evalResult.found_metadata) {
        foundMetadataMap[evalResult.reference_id] = evalResult.found_metadata;
      }
      if (evalResult.reference_id && evalResult.metadata_source) {
        metadataSourceMap[evalResult.reference_id] = evalResult.metadata_source;
      }
      if (evalResult.reference_id && evalResult.validation) {
        validationMap[evalResult.reference_id] = evalResult.validation;
      }
    }
  }

  // Extract OpenAlex ID from the openalex_data.id field (format: "https://openalex.org/W123456")
  const extractOpenalexId = (
    openalexData?: OpenalexDataType,
  ): string | null => {
    if (!openalexData?.id) return null;
    const match = openalexData.id.match(/W(\d+)$/);
    return match ? match[1] : null;
  };

  // Extract best URL from OpenAlex data (prefer PDF, then landing page, then general url)
  const extractOpenalexUrl = (
    openalexData?: OpenalexDataType,
  ): string | null => {
    if (!openalexData) return null;
    // Try to get PDF URL first from locations
    const pdfUrl = openalexData.locations?.find((loc) => loc.pdf_url)?.pdf_url;
    if (pdfUrl) return pdfUrl;
    // Then try landing page URL
    const landingUrl = openalexData.locations?.find(
      (loc) => loc.landing_page_url,
    )?.landing_page_url;
    if (landingUrl) return landingUrl;
    // Fall back to general url field
    return openalexData.url || null;
  };

  const references = (evaluation?.references || []) as ReferenceType[];

  const workContext = evaluation?.work_context as
    | WorkContext
    | null
    | undefined;

  // Count verified vs not verified references
  const verifiedReferencesCount = references.filter((ref) => {
    const openalexId = extractOpenalexId(ref.openalex_data);
    const fullTextUrl = fullTextUrlMap[ref.id];
    const bestSourceUrl = bestSourceUrlMap[ref.id];
    return openalexId || fullTextUrl || bestSourceUrl;
  }).length;
  const notVerifiedCount = referencesCount - verifiedReferencesCount;

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <div style={{ width: '140px' }}>
            {evaluation && (
              <Button secondary size="sm" onClick={reset} icon="add">
                New Analysis
              </Button>
            )}
          </div>
          <Heading as="h1" size="md">
            Reference Evaluation
          </Heading>
          <div style={{ width: '140px' }} />
        </HeaderContent>
      </Header>

      <MainContent>
        {showError && (
          <ErrorBanner>
            <ErrorIcon name="error" size={20} />
            <div>
              <Body size="sm" style={{ fontWeight: 600 }}>
                {evaluation?.status === 'error'
                  ? 'Evaluation Failed'
                  : 'Upload Error'}
              </Body>
              <Body size="sm">{error || evaluation?.error_message}</Body>
              <Button
                secondary
                size="sm"
                onClick={() => {
                  clearError();
                  reset();
                }}
                style={{ marginTop: '8px' }}
              >
                Try Again
              </Button>
            </div>
          </ErrorBanner>
        )}

        {workContext && (
          <WorkContextCard>
            <Heading as="h2" size="xl">
              {workContext.title || 'Untitled Document'}
            </Heading>
            {workContext.authors.length > 0 && (
              <WorkContextAuthors>
                <Icon name="person" size={14} />
                <Body size="sm" color="secondary">
                  {workContext.authors.join(', ')}
                </Body>
              </WorkContextAuthors>
            )}
            {workContext.abstract && (
              <WorkContextAbstract>
                <Body size="sm" color="secondary">
                  {workContext.abstract}
                </Body>
              </WorkContextAbstract>
            )}
          </WorkContextCard>
        )}

        {showUpload && (
          <UploadSection
            className={isDragging ? 'dragging' : ''}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadIcon name="cloud_upload" size={64} />
            <Heading as="h2" size="md" style={{ marginBottom: '8px' }}>
              Upload Your Document
            </Heading>
            <Body size="md" color="secondary" style={{ marginBottom: '24px' }}>
              Drop your PDF or Word document here, or click to browse
            </Body>
            <Body size="sm" color="tertiary">
              We'll extract references, validate them against OpenAlex, and
              evaluate whether they support the claims they're cited for.
            </Body>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
            />
          </UploadSection>
        )}

        {showUploading && (
          <UploadingSection>
            <SpinningIcon name="cloud_upload" size={48} />
            <Heading
              as="h2"
              size="md"
              style={{ marginTop: '16px', marginBottom: '8px' }}
            >
              Uploading Document
            </Heading>
            <Body size="md" color="secondary">
              Please wait while we upload your document...
            </Body>
          </UploadingSection>
        )}

        {showProcessing && evaluation && (
          <ProcessingContainer>
            <ProcessingHeader>
              <SpinningIcon name="sync" size={32} />
              <ProcessingInfo>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '4px',
                  }}
                >
                  <Heading as="h2" size="sm">
                    Analyzing Your Document
                  </Heading>
                  <LiveIndicator>Live</LiveIndicator>
                </div>
                <Body size="sm" color="secondary">
                  {evaluation.current_step || 'Processing...'}
                </Body>
                <ProgressContainer>
                  <ProgressBar>
                    <ProgressFill
                      style={{ width: `${getProgress(evaluation.status)}%` }}
                    />
                  </ProgressBar>
                </ProgressContainer>
              </ProcessingInfo>
              <ProcessingStats>
                <StatBadge>
                  <Icon name="description" size={14} />
                  {referencesCount} references
                </StatBadge>
                <StatBadge>
                  <Icon name="verified" size={14} />
                  {verifiedCount} matched OpenAlex
                </StatBadge>
                <StatBadge>
                  <Icon name="rate_review" size={14} />
                  {evaluationsCount} evaluations
                </StatBadge>
              </ProcessingStats>
            </ProcessingHeader>

            <StatusSteps>
              {PROCESSING_STEPS.map((step) => {
                const isCompleted = isStepCompleted(step, evaluation.status);
                const isActive = isStepActive(step, evaluation.status);
                const StepComponent = isCompleted
                  ? StatusStepCompleted
                  : isActive
                    ? StatusStepActive
                    : StatusStepPending;
                return (
                  <StepComponent key={step.key}>
                    <Icon
                      name={
                        isCompleted
                          ? 'check_circle'
                          : isActive
                            ? 'sync'
                            : 'radio_button_unchecked'
                      }
                      size={16}
                      className={isActive ? 'spinning-step-icon' : ''}
                    />
                    <Body size="xs">{step.label}</Body>
                  </StepComponent>
                );
              })}
            </StatusSteps>

            {/* Streaming references display */}
            {hasReferences && (
              <StreamingSection>
                <StreamingHeader>
                  <Heading as="h3" size="sm">
                    References Found
                  </Heading>
                  <Badge
                    text={`${referencesCount} found`}
                    backgroundColor="var(--brand-colors-blue-100)"
                    color="var(--brand-colors-blue-700)"
                  />
                </StreamingHeader>
                <ReferencesList>
                  {references.map((ref, index) => (
                    <ReferenceCardWrapper
                      key={ref.id}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <ReferenceCard
                        id={ref.id}
                        identifier={ref.identifier}
                        title={ref.title}
                        authors={ref.authors || []}
                        publicationYear={ref.publication_year}
                        publication={ref.publication}
                        doi={ref.doi}
                        citationCount={ref.openalex_data?.citationCount}
                        impactFactor={ref.impact_factor?.impact_factor}
                        journalQuartile={ref.impact_factor?.quartile}
                        claims={ref.claims || []}
                        evaluations={evaluationsMap[ref.id]}
                        isStreaming={isPolling}
                        fullTextUrl={fullTextUrlMap[ref.id]}
                        bestSourceUrl={bestSourceUrlMap[ref.id]}
                        openalexId={extractOpenalexId(ref.openalex_data)}
                        openalexUrl={extractOpenalexUrl(ref.openalex_data)}
                        rawReference={ref.raw_reference}
                        foundMetadata={foundMetadataMap[ref.id]}
                        metadataSource={metadataSourceMap[ref.id]}
                        validation={validationMap[ref.id]}
                      />
                    </ReferenceCardWrapper>
                  ))}
                  {/* Skeleton cards for expected additional references */}
                  {isPolling && referencesCount < 5 && (
                    <SkeletonCard>
                      <SkeletonLine style={{ width: '60%' }} />
                      <SkeletonLine style={{ width: '40%' }} />
                      <SkeletonLine style={{ width: '80%' }} />
                    </SkeletonCard>
                  )}
                </ReferencesList>
              </StreamingSection>
            )}
          </ProcessingContainer>
        )}

        {showResults && evaluation && (
          <ResultsSection>
            <ResultsHeader>
              <div>
                <Heading as="h2" size="lg" style={{ marginBottom: '4px' }}>
                  Evaluation Complete
                </Heading>
                <Body color="secondary">
                  Found {evaluation.references_count} references in your
                  document
                </Body>
              </div>
              <Stats>
                <StatItem>
                  <Icon name="verified" size={20} color="green" />
                  <Body size="sm">{verifiedReferencesCount} verified</Body>
                </StatItem>
                {notVerifiedCount > 0 && (
                  <StatItem>
                    <Icon name="help_outline" size={20} />
                    <Body size="sm">
                      {notVerifiedCount} couldn't be verified
                    </Body>
                  </StatItem>
                )}
                <StatItem>
                  <Icon name="rate_review" size={20} />
                  <Body size="sm">
                    {evaluation.evaluations_count} evaluations
                  </Body>
                </StatItem>
                <Button secondary size="sm" onClick={reset} icon="add">
                  New Evaluation
                </Button>
              </Stats>
            </ResultsHeader>

            <ReferencesList>
              {references.map((ref, index) => (
                <ReferenceCardWrapper
                  key={ref.id}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <ReferenceCard
                    id={ref.id}
                    identifier={ref.identifier}
                    title={ref.title}
                    authors={ref.authors || []}
                    publicationYear={ref.publication_year}
                    publication={ref.publication}
                    doi={ref.doi}
                    citationCount={ref.openalex_data?.citationCount}
                    impactFactor={ref.impact_factor?.impact_factor}
                    journalQuartile={ref.impact_factor?.quartile}
                    claims={ref.claims || []}
                    evaluations={evaluationsMap[ref.id]}
                    fullTextUrl={fullTextUrlMap[ref.id]}
                    bestSourceUrl={bestSourceUrlMap[ref.id]}
                    openalexId={extractOpenalexId(ref.openalex_data)}
                    openalexUrl={extractOpenalexUrl(ref.openalex_data)}
                    rawReference={ref.raw_reference}
                    foundMetadata={foundMetadataMap[ref.id]}
                    metadataSource={metadataSourceMap[ref.id]}
                    validation={validationMap[ref.id]}
                  />
                </ReferenceCardWrapper>
              ))}
            </ReferencesList>
          </ResultsSection>
        )}
      </MainContent>
    </PageContainer>
  );
};

export default ReferenceEvaluationPage;
